import { Badge, Button, buttonClass, cardClass, fieldClass, selectClass, Table } from '@ledgerloom/ui';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, propose, type Outcome } from '../api';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Field } from '../components/Field';
import { Tabs } from '../components/Tabs';
import { Empty, ErrorBanner, Icon, ListSkeleton, outcomeMessage, outcomeTone, ToggleGroup, useToast } from '../components/ui';
import { day, human, money, toMinor } from '../format';
import { useApi, usePageTitle } from '../hooks';
import { can, useSession } from '../session';

// Books (docs/plans/books-and-tickets-build-plan.md, B1 and B2): the chart of accounts,
// the journal, the trial balance, and the statements. Every number comes from the
// server's engines, and every change is a proposal a person signs. Patterns: data-display/table
// for each list and statement, forms/form-validation for an entry's live totals from the engine,
// navigation/tabs for one statement at a time, navigation/navigation-menu (segmented) for as reported.

interface Entity { id: string; name: string; functional_currency: string; fiscal_year_start_month: number; calendar_pattern: 'calendar' | '4-4-5'; retained_earnings_account: string; current_year_earnings_account: string; suspense_account: string }
interface Account { id: string; number: string; name: string; type: string; normal_balance: string; parent: string | null; cash_flow_class: string; statement_line: string; status: 'active' | 'retired'; posted_to: boolean }
interface Period { id: string; entity_id: string; fiscal_year: number; number: number; starts_on: string; ends_on: string; status: 'open' | 'locked' | 'closed'; closed_seq: string | null; closing_entry_id: string | null }
interface EntryLine { position: number; number: string; name: string; debit_minor: number | null; credit_minor: number | null; currency: string; memo: string | null; engine: string | null }
interface Entry { id: string; period_id: string; fiscal_year: number; period_number: number; posting_date: string; entry_type: string; description: string; reversal_of: string | null; reversed_by: string | null; lines: EntryLine[] }
interface Check { debits_minor: number; credits_minor: number; difference_minor: number; balanced: boolean; problems: string[] }
interface TrialBalanceAnswer {
  period: Period; view: 'now' | 'as_reported'; currency: string; engine: { id: string; version: string }; read_to_seq: string; adjustments: number;
  rows: Array<{ number: string; name: string; type: string; debit_minor: number | null; credit_minor: number | null }>;
  totals: { debit_minor: number; credit_minor: number; balanced: boolean };
}

type StatementId = 'balance_sheet' | 'income_statement' | 'cash_flow' | 'changes_in_equity';
interface StatementLine { section: string; key: string; label: string; kind: 'line' | 'total'; amount_minor: number; accounts: Array<{ number: string; name: string; amount_minor: number }> }
interface StatementAnswer {
  statement: StatementId; title: string; view: 'now' | 'as_reported'; from: string; through: string; currency: string; adjustments: number;
  lines: StatementLine[]; checks: Array<{ label: string; difference_minor: number; holds: boolean }>;
}
const STATEMENT_TABS: Array<[StatementId, string]> = [['balance_sheet', 'Balance sheet'], ['income_statement', 'Income statement'], ['cash_flow', 'Cash flow'], ['changes_in_equity', 'Changes in equity']];

const TYPE_WORDS: Record<string, string> = { asset: 'Asset', liability: 'Liability', equity: 'Equity', revenue: 'Revenue', expense: 'Expense', contra: 'Offsets another account' };
const ENTRY_WORDS: Record<string, string> = { standard: 'Standard', adjusting: 'Adjusting', closing: 'Closing', reversing: 'Reversal', opening: 'Opening' };
const FLOW_WORDS: Record<string, string> = { operating: 'Operating', investing: 'Investing', financing: 'Financing', cash: 'Cash', non_cash: 'Not cash' };
export const periodName = (p: Pick<Period, 'fiscal_year' | 'number'>) => (p.number === 13 ? `${p.fiscal_year} adjustments` : `${p.fiscal_year} period ${p.number}`);
// shortcut: today in UTC, as the Point in time page reads it, so a person east of UTC cannot post today's date until UTC reaches it; use the workspace's time zone when a workspace is there.
const today = () => new Date().toISOString().slice(0, 10);

// The entities, and the one being read; a choice of them shows only when there is more than one.
export function useEntity() {
  const entities = useApi<Entity[]>('/v1/books/entities');
  const [chosen, setChosen] = useState<string | null>(null);
  const entity = entities.data?.find((e) => e.id === chosen) ?? entities.data?.[0] ?? null;
  const picker = (entities.data?.length ?? 0) > 1 && (
    <Field label="Entity"><select className={selectClass} value={entity?.id ?? ''} onChange={(e) => setChosen(e.target.value)}>{entities.data!.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select></Field>
  );
  return { entity, picker, loading: entities.loading && !entities.data, error: entities.error };
}

// The fiscal periods, and the one being read: the one holding today until a person picks another.
function usePeriodChoice(entity: Entity | null) {
  const periods = useApi<Period[]>(entity ? `/v1/books/periods?entity_id=${entity.id}` : null, [entity?.id]);
  const [chosen, setChosen] = useState<string | null>(null);
  const current = periods.data?.find((p) => p.number < 13 && p.starts_on <= today() && today() <= p.ends_on) ?? periods.data?.[0];
  const period = periods.data?.find((p) => p.id === chosen) ?? current ?? null;
  const picker = period && (
    <Field label="Through"><select className={selectClass} value={period.id} onChange={(e) => setChosen(e.target.value)}>
      {periods.data!.map((p) => <option key={p.id} value={p.id}>{periodName(p)}, to {day(p.ends_on)} ({human(p.status)})</option>)}
    </select></Field>
  );
  return { periods, period, picker };
}

export function NotSetUp() {
  return <Empty title="Books are not set up yet">The chart of accounts and fiscal calendar arrive with the Books plan, or a person adds them by a signed change.</Empty>;
}

// ---------------------------------------------------------------------------
// Chart of accounts
// ---------------------------------------------------------------------------

export function ChartOfAccounts() {
  usePageTitle('Chart of accounts');
  const { me, refreshStats } = useSession();
  const { entity, picker, loading, error } = useEntity();
  const chart = useApi<{ accounts: Account[] }>(entity ? `/v1/books/accounts?entity_id=${entity.id}` : null, [entity?.id]);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const change = async (accounts: Array<Record<string, unknown>>, ok: string) => {
    if (!entity) return false;
    setBusy(true);
    try {
      const { name, functional_currency, retained_earnings_account, current_year_earnings_account, suspense_account } = entity;
      const out = await propose('config.chart_of_accounts', { entity: { name, functional_currency, retained_earnings_account, current_year_earnings_account, suspense_account }, accounts, reason: ok }, ok);
      toast(outcomeMessage(out, ok), outcomeTone(out));
      refreshStats();
      void chart.reload();
      return out.state !== 'rejected';
    } catch (e) { toast((e as Error).message, 'error'); return false; } finally { setBusy(false); }
  };
  const spec = (a: Account, over: Record<string, unknown>) => ({ number: a.number, name: a.name, type: a.type, normal_balance: a.normal_balance, ...(a.parent ? { parent: a.parent } : {}), cash_flow_class: a.cash_flow_class, statement_line: a.statement_line, status: a.status, ...over });

  return (
    <div className="stack">
      <div className="page-head">
        <div><h1>Chart of accounts</h1><p>Every account your books post to. An account is never deleted: it is retired, and its history stays. Its number and type are fixed once anything has posted to it.</p></div>
        {entity && can(me, 'propose:config') && <Button variant="secondary" aria-pressed={editing} onClick={() => setEditing(!editing)}>{editing ? 'Done' : 'Edit'}</Button>}
      </div>
      {picker}
      <ErrorBanner error={error ?? chart.error} />
      <section className={cardClass} aria-labelledby="chart-h">
        <div className="card-head"><h2 id="chart-h">Accounts</h2>{entity && <span className="faint small">{entity.name}, in {entity.functional_currency}</span>}</div>
        {loading || (entity && chart.loading && !chart.data) ? <ListSkeleton rows={6} label="Loading accounts" /> : !entity ? <NotSetUp /> : (
          <Table label="Chart of accounts">
            <caption className="sr-only">Accounts by number</caption>
            <thead><tr><th scope="col">Number</th><th scope="col">Account</th><th scope="col">Type</th><th scope="col">Cash flow</th>{editing && <th scope="col">Change</th>}</tr></thead>
            <tbody>{chart.data?.accounts.map((a) => (
              <tr key={a.id}>
                <td>{a.number}</td>
                <th scope="row" style={{ fontWeight: 'normal' }}>{a.name}{a.status === 'retired' && <> <Badge tone="neutral">Retired</Badge></>}</th>
                <td>{TYPE_WORDS[a.type]}</td>
                <td>{FLOW_WORDS[a.cash_flow_class]}</td>
                {editing && <td><AccountEdit account={a} busy={busy} onRename={(name) => change([spec(a, { name })], `Rename ${a.number}`)} onRetire={() => change([spec(a, { status: 'retired' })], `Retire ${a.number}`)} /></td>}
              </tr>
            ))}</tbody>
          </Table>
        )}
      </section>
      {editing && chart.data && <NewAccount accounts={chart.data.accounts} busy={busy} onAdd={(a) => change([a], `Add ${a.number}`)} />}
    </div>
  );
}

function AccountEdit({ account, busy, onRename, onRetire }: { account: Account; busy: boolean; onRename: (name: string) => Promise<boolean>; onRetire: () => Promise<boolean> }) {
  const [name, setName] = useState(account.name);
  const [asking, setAsking] = useState(false);
  return (
    <form className="row wrap" style={{ gap: 6 }} onSubmit={(e) => { e.preventDefault(); void onRename(name.trim()); }}>
      <input className={fieldClass} aria-label={`Name of account ${account.number}`} value={name} onChange={(e) => setName(e.target.value)} />
      <Button type="submit" size="sm" variant="secondary" disabled={busy || !name.trim() || name.trim() === account.name}>Rename</Button>
      {account.status === 'active' && <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => setAsking(true)}>Retire</Button>}
      <ConfirmDialog open={asking} title={`Retire ${account.name}?`} confirmLabel="Retire" busy={busy} onCancel={() => setAsking(false)} onConfirm={() => { void onRetire().then(() => setAsking(false)); }}>Nothing can post to it after this. It stays in the chart with its history.</ConfirmDialog>
    </form>
  );
}

function NewAccount({ accounts, busy, onAdd }: { accounts: Account[]; busy: boolean; onAdd: (a: Record<string, unknown> & { number: string }) => Promise<boolean> }) {
  const lines = useMemo(() => [...new Set(accounts.map((a) => a.statement_line))].sort(), [accounts]);
  const [number, setNumber] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState('expense');
  const [flow, setFlow] = useState('operating');
  const [line, setLine] = useState(lines[0] ?? '');
  const [parent, setParent] = useState('');
  const taken = accounts.some((a) => a.number === number);
  const ready = /^[0-9]{4,10}$/.test(number) && !taken && !!name.trim() && !!line && (type !== 'contra' || !!parent);
  return (
    <form className={`${cardClass} card-pad stack`} aria-label="New account" onSubmit={async (e) => {
      e.preventDefault();
      if (await onAdd({ number, name: name.trim(), type, cash_flow_class: flow, statement_line: line, ...(parent ? { parent } : {}) })) { setNumber(''); setName(''); }
    }}>
      <h2 style={{ margin: 0 }}>New account</h2>
      <div className="row wrap" style={{ gap: 12, alignItems: 'flex-start' }}>
        <Field label="Number" required error={taken ? 'That number is in the chart' : null}><input className={fieldClass} inputMode="numeric" value={number} onChange={(e) => setNumber(e.target.value)} /></Field>
        <Field label="Name" required><input className={fieldClass} value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Type"><select className={selectClass} value={type} onChange={(e) => setType(e.target.value)}>{Object.entries(TYPE_WORDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
        {type === 'contra' && <Field label="Offsets" required><select className={selectClass} value={parent} onChange={(e) => setParent(e.target.value)}><option value="">Choose an account</option>{accounts.filter((a) => a.type !== 'contra').map((a) => <option key={a.id} value={a.number}>{a.number} {a.name}</option>)}</select></Field>}
        <Field label="Cash flow"><select className={selectClass} value={flow} onChange={(e) => setFlow(e.target.value)}>{Object.entries(FLOW_WORDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
        <Field label="Statement line"><select className={selectClass} value={line} onChange={(e) => setLine(e.target.value)}>{lines.map((l) => <option key={l} value={l}>{human(l)}</option>)}</select></Field>
      </div>
      <div><Button type="submit" disabled={busy || !ready}>Add account</Button></div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Journal
// ---------------------------------------------------------------------------

interface LineDraft { key: number; account: string; debit: string; credit: string; memo: string }
let nextLine = 0;
const blankLine = (): LineDraft => ({ key: ++nextLine, account: '', debit: '', credit: '', memo: '' });
const positive = (text: string) => { const n = toMinor(text); return Number.isFinite(n) && n > 0 ? n : null; };

export function Journal() {
  usePageTitle('Journal');
  const { me, refreshStats } = useSession();
  const { entity, picker, loading, error } = useEntity();
  const [after, setAfter] = useState<string | null>(null);
  const page = useApi<{ next: string | null; items: Entry[] }>(entity ? `/v1/books/journal?entity_id=${entity.id}${after ? `&after=${encodeURIComponent(after)}` : ''}` : null, [entity?.id, after]);
  const periods = useApi<Period[]>(entity ? `/v1/books/periods?entity_id=${entity.id}` : null, [entity?.id]);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const toast = useToast();
  const mayPropose = can(me, 'propose:gl');
  const holdsToday = periods.data?.some((p) => p.number < 13 && p.starts_on <= today() && today() <= p.ends_on);

  const openYear = async () => {
    try {
      const out = await propose('config.fiscal_calendar', { entity: entity!.name, fiscal_year_start_month: entity!.fiscal_year_start_month, pattern: entity!.calendar_pattern, reason: 'Open this fiscal year' }, 'Open this fiscal year');
      toast(outcomeMessage(out, 'Fiscal year opened'), outcomeTone(out));
      refreshStats();
      void periods.reload();
    } catch (e) { toast((e as Error).message, 'error'); }
  };

  return (
    <div className="stack">
      <div className="page-head">
        <div><h1>Journal</h1><p>Every entry in the books. An entry posts once a person other than its author approves it, and never changes after; a mistake is reversed by a new entry.</p></div>
        {entity && mayPropose && holdsToday && !creating && <Button onClick={() => setCreating(true)}>New entry</Button>}
        {entity && mayPropose && periods.data && !holdsToday && <Button variant="secondary" onClick={() => void openYear()}>Open this fiscal year</Button>}
      </div>
      {picker}
      <ErrorBanner error={error ?? page.error ?? periods.error} />
      {creating && entity && periods.data && (
        <EntryForm entity={entity} periods={periods.data} onCancel={() => setCreating(false)} onDone={(out) => { toast(outcomeMessage(out, 'Posted'), outcomeTone(out)); refreshStats(); setCreating(false); setAfter(null); void page.reload(); }} />
      )}
      <section className={cardClass} aria-labelledby="journal-h">
        <div className="card-head"><h2 id="journal-h">Entries</h2></div>
        {loading || (entity && page.loading && !page.data) ? <ListSkeleton rows={6} label="Loading entries" /> : !entity ? <NotSetUp />
          : !page.data?.items.length ? <Empty title="Nothing has posted yet">{mayPropose ? 'A new entry appears here once it is approved.' : 'Entries appear here once they are approved.'}</Empty> : (
            <Table label="Journal entries">
              <caption className="sr-only">Journal entries, newest first</caption>
              <thead><tr><th scope="col">Date</th><th scope="col">Entry</th><th scope="col">Kind</th><th scope="col">Amount</th><th scope="col"><span className="sr-only">Lines</span></th></tr></thead>
              <tbody>{page.data.items.flatMap((e) => {
                const total = e.lines.reduce((sum, l) => sum + (l.debit_minor ?? 0), 0);
                return [(
                  <tr key={e.id}>
                    <td>{day(e.posting_date)}</td>
                    <th scope="row" style={{ fontWeight: 'normal' }}>{e.description}{e.reversed_by && <> <Badge tone="warning">Reversed</Badge></>}</th>
                    <td>{ENTRY_WORDS[e.entry_type]}</td>
                    <td>{money(total, e.lines[0]?.currency)}</td>
                    <td><Button size="sm" variant="ghost" aria-expanded={open === e.id} onClick={() => setOpen(open === e.id ? null : e.id)}>{open === e.id ? 'Hide lines' : 'Show lines'}<span className="sr-only"> of {e.description}</span></Button></td>
                  </tr>
                ), ...(open === e.id ? [(
                  <tr key={`${e.id}-lines`}><td colSpan={5}><EntryDetail entry={e} periods={periods.data ?? []} mayPropose={mayPropose} onDone={() => { refreshStats(); void page.reload(); }} /></td></tr>
                )] : [])];
              })}</tbody>
            </Table>
          )}
        {page.data?.next && <div className="card-pad"><Button variant="secondary" onClick={() => setAfter(page.data!.next)}>Older entries</Button></div>}
        {after && <div className="card-pad"><Button variant="ghost" onClick={() => setAfter(null)}>Back to the newest</Button></div>}
      </section>
    </div>
  );
}

function EntryDetail({ entry, periods, mayPropose, onDone }: { entry: Entry; periods: Period[]; mayPropose: boolean; onDone: () => void }) {
  const [reversing, setReversing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [date, setDate] = useState(today());
  const toast = useToast();
  const target = periods.find((p) => p.number < 13 && p.status === 'open' && p.starts_on <= date && date <= p.ends_on);
  const canReverse = mayPropose && !entry.reversed_by && entry.entry_type !== 'closing';
  return (
    <div className="stack" style={{ gap: 8 }}>
      <ul className="stack" style={{ gap: 2, margin: 0, padding: 0, listStyle: 'none' }} aria-label={`Lines of ${entry.description}`}>
        {entry.lines.map((l) => (
          <li key={l.position} className="row wrap small" style={{ justifyContent: 'space-between', gap: 12 }}>
            <span>{l.number} {l.name}{l.memo ? `: ${l.memo}` : ''}</span>
            <span>{l.debit_minor !== null ? `Debit ${money(l.debit_minor, l.currency)}` : `Credit ${money(l.credit_minor, l.currency)}`}</span>
          </li>
        ))}
      </ul>
      <div className="faint small">{periodName({ fiscal_year: entry.fiscal_year, number: entry.period_number })}{entry.reversal_of ? ' · Reverses an earlier entry' : ''}{entry.lines[0]?.engine ? ' · Worked out by Mazu' : ''}</div>
      {canReverse && <div><Button size="sm" variant="secondary" onClick={() => setReversing(true)}>Reverse this entry</Button></div>}
      <ConfirmDialog
        open={reversing} title="Reverse this entry?" confirmLabel="Send for approval" busy={busy}
        onCancel={() => setReversing(false)}
        onConfirm={() => {
          if (!target) return;
          setBusy(true);
          propose('gl.journal_entry.reverse', { entry_id: entry.id, period_id: target.id, posting_date: date, reason: `Reverse: ${entry.description}` }, `Reverse: ${entry.description}`)
            .then((out) => { toast(outcomeMessage(out, 'Reversed'), outcomeTone(out)); onDone(); }, (e) => toast((e as Error).message, 'error'))
            .finally(() => { setBusy(false); setReversing(false); });
        }}>
        <p>A new entry posts with every line on the other side, and this one stays, linked to it.</p>
        <Field label="Date of the reversal" error={target ? null : 'No open period holds this date'}><input className={fieldClass} type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} /></Field>
      </ConfirmDialog>
    </div>
  );
}

function EntryForm({ entity, periods, onCancel, onDone }: { entity: Entity; periods: Period[]; onCancel: () => void; onDone: (o: Outcome) => void }) {
  const chart = useApi<{ accounts: Account[] }>(`/v1/books/accounts?entity_id=${entity.id}`, [entity.id]);
  const [date, setDate] = useState(today());
  const [type, setType] = useState<'standard' | 'adjusting' | 'opening'>('standard');
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([blankLine(), blankLine()]);
  const [check, setCheck] = useState<Check | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const period = periods.find((p) => (type === 'adjusting' ? p.number === 13 : p.number < 13) && p.starts_on <= date && date <= p.ends_on);
  const amount = (minor: number) => ({ minor, scale: 4, currency: entity.functional_currency });
  // Only finished lines go to the engine: an account and one amount.
  const finished = lines.flatMap((l) => {
    const [d, c] = [positive(l.debit), positive(l.credit)];
    if (!l.account || (d === null) === (c === null)) return [];
    return [{ account: l.account, ...(d !== null ? { debit: amount(d) } : { credit: amount(c!) }), ...(l.memo.trim() ? { memo: l.memo.trim() } : {}) }];
  });
  const body = JSON.stringify({ entity_id: entity.id, period_id: period?.id, posting_date: date, entry_type: type, lines: finished.map(({ memo: _memo, ...l }) => l) });

  useEffect(() => {
    if (!period) { setCheck(null); return; }
    let live = true;
    const wait = setTimeout(() => {
      api<Check>('/v1/books/journal/check', { body: JSON.parse(body) }).then((c) => { if (live) setCheck(c); }, () => undefined);
    }, 150);
    return () => { live = false; clearTimeout(wait); };
  }, [body, period?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key: number, patch: Partial<LineDraft>) => setLines((all) => all.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const accounts = chart.data?.accounts.filter((a) => a.status === 'active') ?? [];
  const ready = !!period && !!description.trim() && finished.length >= 2 && finished.length === lines.length && !!check?.balanced && !check.problems.length;

  return (
    <form className={`${cardClass} card-pad stack`} aria-label="New journal entry" onSubmit={async (e) => {
      e.preventDefault();
      setBusy(true); setFailure(null);
      try {
        onDone(await propose('gl.journal_entry.post', { entity_id: entity.id, period_id: period!.id, posting_date: date, entry_type: type, description: description.trim(), lines: finished }, description.trim()));
      } catch (err) { setFailure(err); } finally { setBusy(false); }
    }}>
      <h2 style={{ margin: 0 }}>New journal entry</h2>
      <ErrorBanner error={failure ?? chart.error} />
      <div className="row wrap" style={{ gap: 12, alignItems: 'flex-start' }}>
        <Field label="Date" required error={period ? null : 'No fiscal period holds this date'}><input className={fieldClass} type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} /></Field>
        <Field label="Kind"><select className={selectClass} value={type} onChange={(e) => setType(e.target.value as typeof type)}><option value="standard">Standard</option><option value="adjusting">Year-end adjustment</option><option value="opening">Opening balance</option></select></Field>
        <Field label="Description" required className="grow"><input className={fieldClass} value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} /></Field>
      </div>
      <Table label="Lines of the entry">
        <caption className="sr-only">Each line takes an account and one amount, a debit or a credit</caption>
        <thead><tr><th scope="col">Account</th><th scope="col">Debit</th><th scope="col">Credit</th><th scope="col">Note</th><th scope="col"><span className="sr-only">Remove</span></th></tr></thead>
        <tbody>{lines.map((l, i) => (
          <tr key={l.key}>
            <td><select className={selectClass} aria-label={`Account, line ${i + 1}`} value={l.account} onChange={(e) => set(l.key, { account: e.target.value })}><option value="">Choose an account</option>{accounts.map((a) => <option key={a.id} value={a.number}>{a.number} {a.name}</option>)}</select></td>
            <td><input className={fieldClass} inputMode="decimal" aria-label={`Debit, line ${i + 1}`} value={l.debit} onChange={(e) => set(l.key, { debit: e.target.value })} /></td>
            <td><input className={fieldClass} inputMode="decimal" aria-label={`Credit, line ${i + 1}`} value={l.credit} onChange={(e) => set(l.key, { credit: e.target.value })} /></td>
            <td><input className={fieldClass} aria-label={`Note, line ${i + 1}`} value={l.memo} maxLength={200} onChange={(e) => set(l.key, { memo: e.target.value })} /></td>
            <td>{lines.length > 2 && <Button type="button" size="sm" variant="ghost" aria-label={`Remove line ${i + 1}`} onClick={() => setLines((all) => all.filter((x) => x.key !== l.key))}>Remove</Button>}</td>
          </tr>
        ))}</tbody>
      </Table>
      <div><Button type="button" size="sm" variant="secondary" onClick={() => setLines((all) => [...all, blankLine()])}>Add a line</Button></div>
      <div aria-live="polite" className="stack" style={{ gap: 4 }}>
        {check && (check.debits_minor > 0 || check.credits_minor > 0) && <p className="small" style={{ margin: 0 }}>Debits {money(check.debits_minor, entity.functional_currency)} · Credits {money(check.credits_minor, entity.functional_currency)}{check.balanced ? ' · Balanced' : ` · Out by ${money(Math.abs(check.difference_minor), entity.functional_currency)}`}</p>}
        {check?.problems.map((p) => <p key={p} className="small" style={{ margin: 0 }}>{p}</p>)}
      </div>
      <div className="row" style={{ gap: 8 }}>
        <Button type="submit" disabled={busy || !ready}>Send for approval</Button>
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Trial balance
// ---------------------------------------------------------------------------

export function TrialBalance() {
  usePageTitle('Trial balance');
  const { entity, picker, loading, error } = useEntity();
  const { periods, period, picker: periodPicker } = usePeriodChoice(entity);
  const [view, setView] = useState<'now' | 'as_reported'>('now');
  const [closing, setClosing] = useState<'before' | 'after'>('before');
  const reported = !!period?.closed_seq && view === 'as_reported';
  const after = !!period?.closing_entry_id && !reported && closing === 'after';
  const tb = useApi<TrialBalanceAnswer>(period ? `/v1/books/trial-balance?entity_id=${entity!.id}&period_id=${period.id}${reported ? '&view=as_reported' : ''}${after ? '&after_closing=true' : ''}` : null, [period?.id, reported, after]);
  const currency = tb.data?.currency ?? entity?.functional_currency;

  return (
    <div className="stack">
      <div className="page-head"><div><h1>Trial balance</h1><p>Each account's balance at the end of a period. Debits and credits always match: if they ever did not, the books would be wrong, and the total below would say so.</p></div></div>
      {picker}
      <ErrorBanner error={error ?? periods.error ?? tb.error} />
      <section className={cardClass} aria-labelledby="tb-h">
        <div className="card-head"><h2 id="tb-h">Balances</h2></div>
        {loading || (entity && periods.loading && !periods.data) ? <ListSkeleton rows={6} label="Loading the trial balance" /> : !entity || !period ? <NotSetUp /> : (
          <>
            <div className="card-pad row wrap" style={{ gap: 12, alignItems: 'flex-end' }}>
              {periodPicker}
              {period.closed_seq && <ToggleGroup label="Which figures" value={view} onChange={setView} options={[['now', 'As known now'], ['as_reported', 'As reported']]} />}
              {period.closing_entry_id && !reported && <ToggleGroup label="Year-end closing entry" value={closing} onChange={setClosing} options={[['before', 'Before closing'], ['after', 'After closing']]} />}
            </div>
            {period.closed_seq && tb.data && tb.data.adjustments > 0 && <p className="small card-pad" style={{ margin: 0 }}>{tb.data.adjustments === 1 ? 'One change' : `${tb.data.adjustments} changes`} entered after this month closed took effect inside it. <Link to="/ledger">See them on Point in time</Link></p>}
            {tb.loading && !tb.data ? <ListSkeleton rows={6} label="Loading balances" /> : !tb.data?.rows.length ? <Empty title="Nothing has posted through this period" /> : (
              <div aria-live="polite">
                <Table label="Trial balance">
                  <caption className="sr-only">Balance of each account through {periodName(period)}</caption>
                  <thead><tr><th scope="col">Number</th><th scope="col">Account</th><th scope="col">Debit</th><th scope="col">Credit</th></tr></thead>
                  <tbody>{tb.data.rows.map((r) => (
                    <tr key={r.number}>
                      <td>{r.number}</td>
                      <th scope="row" style={{ fontWeight: 'normal' }}>{r.name}</th>
                      <td>{r.debit_minor !== null ? money(r.debit_minor, currency) : ''}</td>
                      <td>{r.credit_minor !== null ? money(r.credit_minor, currency) : ''}</td>
                    </tr>
                  ))}</tbody>
                  <tfoot><tr><th scope="row" colSpan={2}>Total</th><td>{money(tb.data.totals.debit_minor, currency)}</td><td>{money(tb.data.totals.credit_minor, currency)}</td></tr></tfoot>
                </Table>
                <p className="card-pad small" style={{ margin: 0 }}>{tb.data.totals.balanced ? <Badge tone="success">Balanced</Badge> : <Badge tone="danger">Out of balance</Badge>}</p>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Statements
// ---------------------------------------------------------------------------

export function Statements() {
  usePageTitle('Financial statements');
  const { entity, picker, loading, error } = useEntity();
  const { periods, period, picker: periodPicker } = usePeriodChoice(entity);
  const [view, setView] = useState<'now' | 'as_reported'>('now');
  const [tab, setTab] = useState<StatementId>('balance_sheet');
  const reported = !!period?.closed_seq && view === 'as_reported';
  const query = period ? `entity_id=${entity!.id}&period_id=${period.id}${reported ? '&view=as_reported' : ''}` : '';
  const answer = useApi<StatementAnswer>(period ? `/v1/books/statements/${tab}?${query}` : null, [tab, query]);
  const shown = answer.data;

  return (
    <div className="stack">
      <div className="page-head">
        <div><h1>Financial statements</h1><p>What the books say, for the fiscal year to date. Each line names the accounts it adds up.</p></div>
        {period && <a className={buttonClass({ variant: 'secondary' })} href={`/v1/books/statements/${tab}/csv?${query}`} download><Icon name="download" />Download CSV</a>}
      </div>
      {picker}
      <ErrorBanner error={error ?? periods.error ?? answer.error} />
      {loading || (entity && periods.loading && !periods.data) ? <ListSkeleton rows={6} label="Loading the statements" /> : !entity || !period ? <section className={cardClass}><NotSetUp /></section> : (
        <>
          <div className="row wrap" style={{ gap: 12, alignItems: 'flex-end' }}>
            {periodPicker}
            {period.closed_seq && <ToggleGroup label="Which figures" value={view} onChange={setView} options={[['now', 'Restated'], ['as_reported', 'As reported']]} />}
          </div>
          {period.closed_seq && shown && shown.adjustments > 0 && <p className="small" style={{ margin: 0 }}>{shown.adjustments === 1 ? 'One change' : `${shown.adjustments} changes`} entered after this month closed took effect inside it. <Link to="/ledger">See them on Point in time</Link></p>}
          <Tabs label="Statement" value={tab} tabs={STATEMENT_TABS} onChange={setTab}>
            <section className={cardClass} aria-label={STATEMENT_TABS.find(([id]) => id === tab)![1]}>
              {!shown ? !answer.error && <ListSkeleton rows={6} label="Loading the statement" /> : !shown.lines.some((l) => l.accounts.length) ? <Empty title="Nothing has posted through this period" /> : <StatementTable answer={shown} />}
            </section>
          </Tabs>
        </>
      )}
    </div>
  );
}

function StatementTable({ answer }: { answer: StatementAnswer }) {
  const sections = [...new Set(answer.lines.map((l) => l.section))];
  return (
    <div aria-live="polite">
      <p className="card-pad small faint" style={{ margin: 0 }}>{answer.statement === 'balance_sheet' ? `At ${day(answer.through)}` : `${day(answer.from)} to ${day(answer.through)}`}, in {answer.currency}</p>
      <Table label={answer.title}>
        <caption className="sr-only">{answer.title}</caption>
        <thead><tr><th scope="col">Line</th><th scope="col">Amount</th></tr></thead>
        {sections.map((section) => (
          <tbody key={section}>
            <tr><th scope="rowgroup" colSpan={2}>{section}</th></tr>
            {answer.lines.filter((l) => l.section === section).map((l) => (
              <tr key={l.key}>
                <th scope="row" style={{ fontWeight: l.kind === 'total' ? undefined : 'normal' }}>
                  {l.label}
                  {!!l.accounts.length && <div className="faint small">{l.accounts.map((a) => `${a.number} ${a.name} ${money(a.amount_minor, answer.currency)}`).join(' · ')}</div>}
                </th>
                <td>{l.kind === 'total' ? <strong>{money(l.amount_minor, answer.currency)}</strong> : money(l.amount_minor, answer.currency)}</td>
              </tr>
            ))}
          </tbody>
        ))}
      </Table>
      <div className="card-pad row wrap small" style={{ gap: 8 }}>
        {answer.checks.map((c) => <Badge key={c.label} tone={c.holds ? 'success' : 'danger'}>{c.holds ? c.label : `Does not tie, by ${money(Math.abs(c.difference_minor), answer.currency)}: ${c.label}`}</Badge>)}
      </div>
    </div>
  );
}
